import type {CustomFontOptions} from '@stripe-internal/connect-embedded-lib';
import {
  CONNECT_ELEMENTS_OVERLAYS_DEFAULT,
  CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
  getAppearanceValuesWithDefaults,
} from '@stripe-internal/embedded-theming';
import type {
  UpdateOptions,
  MetaOptions,
  InitOptions,
  USE_FOR_ANALYTICS_ONLY_HostApp,
} from './ConnectJSInterface/InitAndUpdateOptionsTypes';

export type ReducedMetaOptions = Omit<MetaOptions, 'hostApp'> & {
  /**
   * The hostApp specified by the UI surface embedding ConnectJS. This value is used for analytics purposes only.
   * Do not use this value to change the behavior of the components.
   * https://trailhead.corp.stripe.com/docs/connect-integration-guide/connect-embedded-components/embedded-components-in-stripe-owned-surfaces#hostapp-should-only-be-used-for-analytics-purposes
   */
  USE_FOR_ANALYTICS_ONLY_hostApp?: USE_FOR_ANALYTICS_ONLY_HostApp;
};

/**
 * ConnectJS options with default values filled in
 */
export type IConnectJsOptionsWithDefaultValues = Required<UpdateOptions> & {
  metaOptions: ReducedMetaOptions;
  locale: string;
  fonts: CustomFontOptions;
};

/**
 * ConnectJS options that are specified by the platform
 */
export type IPlatformSpecifiedConnectJsOptions = Omit<
  InitOptions,
  'clientSecret' | 'publishableKey' | 'refreshClientSecret'
>;

export const defaultConnectJsOptions = {
  appearance: {
    overlays: CONNECT_ELEMENTS_OVERLAYS_DEFAULT,
    variables: {
      overlayZIndex: CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
    },
  },
  metaOptions: {},
  locale: '',
  sdkOptions: {},
  fonts: [],
  displayOptions: {},
};

export class ConnectJsOptions {
  private observers: ((values: IPlatformSpecifiedConnectJsOptions) => void)[];

  private platformSpecifiedValues: IPlatformSpecifiedConnectJsOptions;

  private calculatedValues: IConnectJsOptionsWithDefaultValues;

  /**
   * Initialize with ConnectJS init options and overrides
   */
  constructor(
    initOptions: IPlatformSpecifiedConnectJsOptions,
    initOverrides?: Partial<InitOptions> | undefined,
  ) {
    this.observers = [];
    // We consider init overrides as part of the platform specified values
    this.platformSpecifiedValues = {...initOptions, ...initOverrides};
    this.calculatedValues = this.calculateInitValuesWithDefaults();
  }

  public getPlatformSpecifiedValues(): IPlatformSpecifiedConnectJsOptions {
    return this.platformSpecifiedValues;
  }

  public getValues(): IConnectJsOptionsWithDefaultValues {
    return this.calculatedValues;
  }

  private calculateInitValuesWithDefaults(): IConnectJsOptionsWithDefaultValues {
    const {appearance, metaOptions, locale, fonts, displayOptions} =
      this.platformSpecifiedValues;

    // Copy all properties except hostApp
    const {hostApp, ...metaOptionsWithoutHostApp} = metaOptions ?? {};
    const reducedMetaOptions: ReducedMetaOptions = {
      USE_FOR_ANALYTICS_ONLY_hostApp: hostApp,
      ...metaOptionsWithoutHostApp,
    };

    const calculatedAppearanceVariables: IConnectJsOptionsWithDefaultValues = {
      appearance: getAppearanceValuesWithDefaults(
        appearance ?? defaultConnectJsOptions.appearance,
        metaOptions?.mobileSdk,
      ),
      metaOptions: reducedMetaOptions,
      locale: locale ?? '',
      sdkOptions: metaOptions?.sdkOptions ?? {},
      fonts: fonts ?? [],
      displayOptions: displayOptions ?? {},
    };

    return calculatedAppearanceVariables;
  }

  public updateValues(newValues: UpdateOptions) {
    // Simply update existing values with new values
    this.platformSpecifiedValues = {
      ...this.platformSpecifiedValues,
      ...newValues,
    };
    this.calculatedValues = this.calculateInitValuesWithDefaults();
    // eslint-disable-next-line no-restricted-syntax
    for (const observer of this.observers) {
      observer(this.platformSpecifiedValues);
    }
  }

  public registerObserver(observer: () => void) {
    this.observers.push(observer);
  }

  public unregisterObserver(observer: () => void) {
    this.observers = this.observers.filter((o) => o !== observer);
  }
}
